import React, { useState, useRef } from "react";

// Sample user data - replace with your actual data
const initialUsers = [
  {
    id: 1,
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@example.com",
    location: "London, UK",
    phone: "+44 7700 900123",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format",
    role: "Manager",
    permissions: {
      schedule: true,
      timeTracking: true,
      notes: false,
      requests: true,
      jobs: true,
      reports: false,
    }
  },
  {
    id: 2,
    firstName: "Jane",
    lastName: "Smith",
    email: "jane.smith@example.com",
    location: "Manchester, UK",
    phone: "+44 7700 900456",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b1e5?w=40&h=40&fit=crop&crop=face&auto=format",
    role: "Employee",
    permissions: {
      schedule: true,
      timeTracking: true,
      notes: true,
      requests: false,
      jobs: false,
      reports: false,
    }
  },
  {
    id: 3,
    firstName: "Mike",
    lastName: "Johnson",
    email: "mike.johnson@example.com",
    location: "Birmingham, UK",
    phone: "+44 7700 900789",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face&auto=format",
    role: "Employee",
    permissions: {
      schedule: false,
      timeTracking: true,
      notes: false,
      requests: false,
      jobs: true,
      reports: false,
    }
  }
];

function Users() {
  const [users, setUsers] = useState(initialUsers);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    location: '',
    phone: '',
    role: 'Employee',
    permissions: {
      schedule: false,
      timeTracking: false,
      notes: false,
      requests: false,
      jobs: false,
      reports: false,
    }
  });

  const fileInputRef = useRef(null);

  const handleDivClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissionChange = (permission, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: value
      }
    }));
  };

  const handleCreateUser = () => {
    setIsCreating(true);
    setSelectedUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      location: '',
      phone: '',
      role: 'Employee',
      permissions: {
        schedule: false,
        timeTracking: false,
        notes: false,
        requests: false,
        jobs: false,
        reports: false,
      }
    });
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsCreating(false);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      location: user.location,
      phone: user.phone,
      role: user.role,
      permissions: user.permissions
    });
  };

  const handleSaveUser = () => {
    if (isCreating) {
      // Add new user
      const newUser = {
        id: users.length + 1,
        ...formData,
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format"
      };
      setUsers(prev => [...prev, newUser]);
    } else {
      // Update existing user
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id ? { ...user, ...formData } : user
      ));
    }
    setSelectedUser(null);
    setIsCreating(false);
  };

  const handleDeleteUser = (userId) => {
    setUsers(prev => prev.filter(user => user.id !== userId));
  };

  const permissionSections = [
    {
      id: "schedule",
      label: "Schedule",
      description: "Access to schedule management features",
    },
    {
      id: "timeTracking",
      label: "Time tracking and timesheets",
      description: "Access to time tracking functionality",
    },
    {
      id: "notes",
      label: "Notes",
      description: "Access to notes across the platform",
    },
    {
      id: "requests",
      label: "Requests",
      description: "Access to client requests and custom fields",
    },
    {
      id: "jobs",
      label: "Jobs",
      description: "Access to job management features",
    },
    {
      id: "reports",
      label: "Reports",
      description: "Access to reporting functionality",
    },
  ];

  if (selectedUser || isCreating) {
    return (
      <div>
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {isCreating ? 'Create New User' : 'Edit User'}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {isCreating ? 'Add a new user to your system.' : 'Update user information and permissions.'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setIsCreating(false);
                }}
                className="px-4 py-2 flex items-center cursor-pointer gap-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to Users
              </button>
              <button
                onClick={handleSaveUser}
                className="px-4 py-2 flex items-center cursor-pointer gap-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save User
              </button>
            </div>
          </div>
        </div>

        {/* Personal Info Section */}
        <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
          <div className="min-w-[200px] max-w-[280px] flex flex-1 justify-start items-start flex-col">
            <div className="flex justify-start items-center flex-row gap-0.5">
              <span className="text-[#414651] text-sm font-semibold">
                Personal info
              </span>
            </div>
            <p className="self-stretch text-[#535862] text-sm">
              Update user photo and personal details.
            </p>
          </div>
          <div className="min-w-[360px] max-w-[720px] flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
            <div className="flex self-stretch justify-start items-start flex-col gap-6 p-[24px]">
              <div className="flex self-stretch justify-start items-start flex-row gap-6">
                <div className="flex flex-1 justify-start items-start flex-col gap-1.5">
                  <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                    <div className="flex justify-start items-start flex-row gap-0.5">
                      <span className="text-[#414651] text-sm font-medium">
                        First name
                      </span>
                      <span className="text-[#009C97] text-sm font-medium">
                        *
                      </span>
                    </div>
                    <div className="flex self-stretch justify-start items-center flex-row gap-2 py-2.5 px-3.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                      <div className="flex flex-1 justify-start items-center flex-row gap-2">
                        <input
                          type="text"
                          placeholder="First name.."
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className="outline-none w-full text-gray-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-1 justify-start items-start flex-col gap-1.5">
                  <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                    <div className="flex justify-start items-start flex-row gap-0.5">
                      <span className="text-[#414651] text-sm font-medium">
                        Last name
                      </span>
                      <span className="text-[#009C97] text-sm font-medium">
                        *
                      </span>
                    </div>
                    <div className="flex self-stretch justify-start items-center flex-row gap-2 py-2.5 px-3.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                      <div className="flex flex-1 justify-start items-center flex-row gap-2">
                        <input
                          type="text"
                          placeholder="Last name.."
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className="outline-none w-full text-gray-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-start items-start flex-col gap-1.5 w-full">
                <div className="flex justify-start items-start flex-row gap-0.5">
                  <span className="text-[#414651] text-sm font-medium">
                    Email
                  </span>
                  <span className="text-[#009C97] text-sm font-medium">*</span>
                </div>
                <div className="flex self-stretch justify-start items-center flex-row gap-2 py-2.5 px-3.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <input
                    type="email"
                    placeholder="Email address.."
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="outline-none w-full text-gray-600"
                  />
                </div>
              </div>
              <div className="flex justify-start items-start flex-col gap-1.5 w-full">
                <div className="flex justify-start items-start flex-row gap-0.5">
                  <span className="text-[#414651] text-sm font-medium">
                    Location
                  </span>
                  <span className="text-[#009C97] text-sm font-medium">*</span>
                </div>
                <div className="flex self-stretch justify-start items-center flex-row gap-2 py-2.5 px-3.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <input
                    type="text"
                    placeholder="Enter address.."
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="outline-none w-full text-gray-600"
                  />
                </div>
              </div>
              <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                  <div className="flex justify-start items-start flex-row gap-0.5">
                    <span className="text-[#414651] text-sm font-medium">
                      Phone Number
                    </span>
                    <span className="text-[#009C97] text-sm font-medium">*</span>
                  </div>
                  <div className="flex self-stretch justify-start items-start flex-row bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                    <div className="flex self-stretch justify-start items-center text-gray-700 gap-1 py-2.5 pl-3.5">
                      <select>
                        <option value="UK" className="text-gray-600">
                          UK (+44)
                        </option>
                      </select>
                    </div>
                    <div className="flex flex-1 justify-start items-center flex-row gap-2 py-2.5 pr-3.5 pl-3">
                      <input
                        type="tel"
                        placeholder="7700 900123"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="text-gray-700 w-full outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-start items-start flex-col gap-1.5 w-full">
                <div className="flex justify-start items-start flex-row gap-0.5">
                  <span className="text-[#414651] text-sm font-medium">
                    Role
                  </span>
                  <span className="text-[#009C97] text-sm font-medium">*</span>
                </div>
                <div className="flex self-stretch justify-start items-center flex-row gap-2 py-2.5 px-3.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="outline-none w-full text-gray-600"
                  >
                    <option value="Employee">Employee</option>
                    <option value="Manager">Manager</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex self-stretch justify-start items-start flex-row gap-5">
                <div className="flex justify-center items-center flex-row rounded-full">
                  <img 
                    src={selectedUser?.avatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format"} 
                    alt="Avatar" 
                    className="w-10 h-10 rounded-full"
                  />
                </div>
                <div className="flex flex-1 justify-start items-start flex-col gap-4">
                  <div
                    onClick={handleDivClick}
                    className="flex self-stretch cursor-pointer justify-start items-center flex-col gap-1 py-4 px-[24px] bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl"
                  >
                    <div className="flex self-stretch justify-start items-center flex-col gap-3">
                      <div
                        className="flex justify-center items-center flex-row p-2.5 bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] w-[40px] h-[40px]"
                        style={{ width: "40px" }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.66669 13.3333L10 10M10 10L13.3334 13.3333M10 10V17.5M16.6667 13.9524C17.6846 13.1117 18.3334 11.8399 18.3334 10.4167C18.3334 7.88536 16.2813 5.83333 13.75 5.83333C13.5679 5.83333 13.3976 5.73833 13.3051 5.58145C12.2184 3.73736 10.212 2.5 7.91669 2.5C4.46491 2.5 1.66669 5.29822 1.66669 8.75C1.66669 10.4718 2.36289 12.0309 3.48914 13.1613"
                            stroke="#414651"
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="flex self-stretch justify-start items-center flex-col gap-1">
                        <div className="flex self-stretch justify-center items-start flex-row gap-1">
                          <div className="flex justify-center items-center flex-row gap-1.5">
                            <span className="text-[#009C97] text-sm font-semibold">
                              Click to upload
                            </span>
                          </div>
                          <span className="text-[#535862] text-sm">
                            or drag and drop
                          </span>
                        </div>
                        <p className="self-stretch text-[#535862] text-xs text-center">
                          SVG, PNG, JPG or GIF (max. 800x400px)
                        </p>
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/png,image/jpeg,image/gif,image/svg+xml"
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="self-stretch bg-[#E9EAEB] h-px my-10"></div>

        {/* Permissions Section */}
        <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
          <div className="min-w-[200px] max-w-[280px] flex flex-1 justify-start items-start flex-col">
            <div className="flex justify-start items-center flex-row gap-0.5">
              <span className="text-[#414651] text-sm font-semibold">
                Permissions
              </span>
            </div>
            <p className="self-stretch text-[#535862] text-sm">
              Manage what this user can do on the platform
            </p>
          </div>
          <div className="min-w-[320px] max-w-[720px] flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
            <div className="flex self-stretch justify-start items-start flex-col gap-6 p-[24px]">
              <div className="w-full flex items-start gap-5 flex-col">
                <div className="w-full flex flex-col gap-5">
                  {permissionSections.map((section) => (
                    <div
                      key={section.id}
                      className="flex flex-col gap-2 border-b border-gray-200 pb-4 last:border-b-0"
                    >
                      <div className="flex justify-between items-center w-full">
                        <label className="text-gray-800 text-lg font-semibold">
                          {section.label}
                        </label>
                        <div
                          onClick={() =>
                            handlePermissionChange(section.id, !formData.permissions[section.id])
                          }
                          className={`w-10 h-5 rounded-full shadow-sm relative transition-colors duration-300 ease-in-out ${
                            formData.permissions[section.id]
                              ? "bg-[#19BFB7]"
                              : "bg-gray-100"
                          } px-[3px] cursor-pointer`}
                        >
                          <span
                            className={`rounded-full w-3 h-3 transition-transform duration-200 bg-white ${
                              formData.permissions[section.id]
                                ? "translate-x-5"
                                : "translate-x-0"
                            } shadow-sm absolute -translate-y-1/2 top-1/2`}
                          ></span>
                        </div>
                      </div>
                      {section.description && (
                        <p className="text-gray-500 font-semibold text-sm">
                          {section.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex self-stretch justify-start items-center flex-col gap-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-end w-full gap-3 px-6">
                <button 
                  onClick={handleSaveUser}
                  className="bg-[#009C97] text-white rounded-lg px-3 py-1.5 cursor-pointer font-semibold text-sm"
                >
                  Save changes
                </button>
                <button 
                  onClick={() => {
                    setSelectedUser(null);
                    setIsCreating(false);
                  }}
                  className="text-gray-700 border border-gray-300 hover:bg-gray-100 transition duration-150 rounded-lg px-3 py-1.5 cursor-pointer font-semibold text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              User Management
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Here is a complete overview of your users.
            </p>
          </div>
          <button
            onClick={handleCreateUser}
            className="px-4 py-2 flex items-center cursor-pointer gap-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add User
          </button>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white border border-[#E9EAEB] rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
        <div className="p-6">
          <div className="space-y-4">
            {users.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={user.avatar}
                    alt={`${user.firstName} ${user.lastName}`}
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <p className="text-sm text-gray-500">{user.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{user.role}</p>
                    <p className="text-sm text-gray-600">{user.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;